/**
 * VisualizationEngine — Orchestrates the render loop, manages transitions,
 * and connects the audio analyzer to the active visualization.
 *
 * This is the central coordinator. It:
 * 1. Runs a requestAnimationFrame loop at 60fps.
 * 2. Each frame: reads audio data → builds FrameData → calls active visualization's render().
 * 3. Handles crossfade transitions between visualizations using dual canvases.
 * 4. Supports blackout, freeze, and brightness controls.
 * 5. Communicates frame data to the projection window via BroadcastChannel.
 */

import { AudioAnalyzer } from './AudioAnalyzer';
import { AudioSourceManager } from './AudioSourceManager';
import { FrameData, Visualization, VisualizerState } from './types';
import { allVisualizations, getAllVisualizationsWithCustom, createVisualizationById } from './visualizations';

/** Default state for the visualizer */
export const DEFAULT_VISUALIZER_STATE: VisualizerState = {
  activeVisualizationId: 'frequency-bars',
  mode: 'manual',
  timedIntervalSec: 30,
  poolIds: allVisualizations.map(v => v.id),
  crossfadeSec: 1.5,
  brightness: 1,
  running: false,
  blackout: false,
  frozen: false,
  showFps: false,
  performanceMode: false,
};

const VISUALIZER_DEFAULTS_KEY = 'votebeats_visualizer_defaults';

/** Fields to persist (exclude runtime-only states) */
const PERSISTED_FIELDS: (keyof VisualizerState)[] = [
  'activeVisualizationId', 'mode', 'timedIntervalSec', 'poolIds',
  'crossfadeSec', 'brightness', 'showFps', 'performanceMode',
];

const AUDIO_SOURCE_KEY = 'votebeats_visualizer_audio_source';

/** Save the preferred audio source type */
export function savePreferredAudioSource(source: 'system' | 'microphone'): void {
  localStorage.setItem(AUDIO_SOURCE_KEY, source);
}

/** Load the preferred audio source type (defaults to 'system') */
export function loadPreferredAudioSource(): 'system' | 'microphone' {
  const saved = localStorage.getItem(AUDIO_SOURCE_KEY);
  return saved === 'microphone' ? 'microphone' : 'system';
}

/** Load saved visualizer defaults from localStorage */
export function loadSavedDefaults(): VisualizerState {
  try {
    const raw = localStorage.getItem(VISUALIZER_DEFAULTS_KEY);
    if (raw) {
      const saved = JSON.parse(raw);
      return { ...DEFAULT_VISUALIZER_STATE, ...saved, running: false, blackout: false, frozen: false };
    }
  } catch {
    // Corrupt data — ignore
  }
  return { ...DEFAULT_VISUALIZER_STATE };
}

/** Save current visualizer settings as defaults in localStorage */
export function saveVisualizerDefaults(state: VisualizerState): void {
  const toSave: Partial<VisualizerState> = {};
  for (const key of PERSISTED_FIELDS) {
    (toSave as Record<string, unknown>)[key] = state[key];
  }
  localStorage.setItem(VISUALIZER_DEFAULTS_KEY, JSON.stringify(toSave));
}

/** Clear saved visualizer defaults (reset to system defaults) */
export function clearVisualizerDefaults(): void {
  localStorage.removeItem(VISUALIZER_DEFAULTS_KEY);
}

export class VisualizationEngine {
  private audioSource: AudioSourceManager;
  private analyzer: AudioAnalyzer | null = null;
  private animFrameId: number | null = null;
  private state: VisualizerState;
  private onStateChange: ((state: VisualizerState) => void) | null = null;

  // ── Canvas and rendering ────────────────────────────────────────────────
  /** Primary canvas where the active visualization renders */
  private canvas: HTMLCanvasElement | null = null;
  private activeViz: Visualization | null = null;

  // ── Crossfade transition ────────────────────────────────────────────────
  private transitionCanvas: HTMLCanvasElement | null = null;
  private transitionCtx: CanvasRenderingContext2D | null = null;
  private outgoingViz: Visualization | null = null;
  private outgoingCanvas: HTMLCanvasElement | null = null;
  private transitionProgress = 0; // 0 = showing old, 1 = showing new
  private transitionActive = false;

  // ── Auto rotation ───────────────────────────────────────────────────────
  private autoTimerInterval: ReturnType<typeof setInterval> | null = null;
  private lastSongId: string | null = null;

  // ── FPS tracking ────────────────────────────────────────────────────────
  private fpsFrames = 0;
  private fpsLastTime = 0;
  private currentFps = 0;

  // ── BroadcastChannel for projection window ──────────────────────────────
  private channel: BroadcastChannel | null = null;

  // ── Silent frame for when no audio is connected ─────────────────────────
  private silentFreqData = new Uint8Array(1024);
  private silentTimeData = new Uint8Array(1024).fill(128);

  constructor() {
    this.audioSource = new AudioSourceManager();
    this.state = loadSavedDefaults();

    // Set up BroadcastChannel for projection window communication
    try {
      this.channel = new BroadcastChannel('votebeats-visualizer');

      // Respond to state requests from the projection window
      this.channel.onmessage = (event) => {
        if (event.data?.type === 'request-state') {
          this.safeBroadcast({ type: 'state', state: this.state });
        }
      };
    } catch {
      // BroadcastChannel not supported — projection window won't work
    }

    this.audioSource.onEnded(() => {
      this.analyzer = null;
      // Notify listeners that audio was disconnected
      this.safeBroadcast({ type: 'audio-disconnected' });
    });
  }

  // ── Public API ──────────────────────────────────────────────────────────

  get audioManager(): AudioSourceManager {
    return this.audioSource;
  }

  getState(): VisualizerState {
    return { ...this.state };
  }

  getFps(): number {
    return this.currentFps;
  }

  setStateChangeCallback(cb: (state: VisualizerState) => void): void {
    this.onStateChange = cb;
  }

  /** Attach the engine to a canvas element. Must be called before start(). */
  attachCanvas(canvas: HTMLCanvasElement): void {
    this.canvas = canvas;

    // Create an offscreen canvas for crossfade transitions
    this.transitionCanvas = document.createElement('canvas');
    this.transitionCtx = this.transitionCanvas.getContext('2d');

    // Create a second offscreen canvas for the outgoing visualization during transition
    this.outgoingCanvas = document.createElement('canvas');
  }

  /** Start the audio source (system audio or microphone) */
  async startAudio(source: 'system' | 'microphone'): Promise<void> {
    if (source === 'system') {
      await this.audioSource.startSystemAudio();
    } else {
      await this.audioSource.startMicrophone();
    }

    const analyserNode = this.audioSource.analyser;
    if (analyserNode) {
      this.analyzer = new AudioAnalyzer(analyserNode);
    }
  }

  /** Start the render loop */
  start(): void {
    if (!this.canvas) return;

    // Initialize the active visualization
    const vizId = this.state.activeVisualizationId || 'frequency-bars';
    this.setVisualization(vizId);

    this.updateState({ running: true });
    this.startAutoRotation();
    this.renderLoop();
  }

  /** Stop the render loop and clean up */
  stop(): void {
    if (this.animFrameId !== null) {
      cancelAnimationFrame(this.animFrameId);
      this.animFrameId = null;
    }
    this.stopAutoRotation();
    this.activeViz?.destroy();
    this.activeViz = null;
    this.outgoingViz?.destroy();
    this.outgoingViz = null;
    this.audioSource.stop();
    this.analyzer = null;
    this.updateState({ running: false });
  }

  /** Fully destroy the engine and release all resources including the BroadcastChannel */
  destroy(): void {
    this.stop();
    this.channel?.close();
    this.channel = null;
  }

  /** Switch to a specific visualization (with crossfade if running) */
  setVisualization(id: string): void {
    const viz = createVisualizationById(id);
    if (!viz || !this.canvas) return;

    if (this.activeViz && this.state.running && this.state.crossfadeSec > 0) {
      // Start crossfade transition
      this.startTransition(viz);
    } else {
      // Immediate switch (no transition)
      this.activeViz?.destroy();
      this.activeViz = viz;
      this.activeViz.init(this.canvas);
      this.activeViz.resize(this.canvas.width, this.canvas.height);
    }

    this.updateState({ activeVisualizationId: id });
  }

  /** Go to next visualization in the pool */
  nextVisualization(): void {
    const pool = this.getPool();
    if (pool.length === 0) return;
    const currentIdx = pool.findIndex(v => v.id === this.state.activeVisualizationId);
    const nextIdx = (currentIdx + 1) % pool.length;
    this.setVisualization(pool[nextIdx].id);
  }

  /** Go to previous visualization in the pool */
  prevVisualization(): void {
    const pool = this.getPool();
    if (pool.length === 0) return;
    const currentIdx = pool.findIndex(v => v.id === this.state.activeVisualizationId);
    const prevIdx = (currentIdx - 1 + pool.length) % pool.length;
    this.setVisualization(pool[prevIdx].id);
  }

  /** Pick a random visualization from the pool */
  randomVisualization(): void {
    const pool = this.getPool().filter(v => v.id !== this.state.activeVisualizationId);
    if (pool.length === 0) return;
    const idx = Math.floor(Math.random() * pool.length);
    this.setVisualization(pool[idx].id);
  }

  /** Update visualizer state */
  updateState(partial: Partial<VisualizerState>): void {
    this.state = { ...this.state, ...partial };
    this.onStateChange?.(this.state);

    // Handle mode changes
    if ('mode' in partial || 'timedIntervalSec' in partial) {
      this.stopAutoRotation();
      this.startAutoRotation();
    }

    // Broadcast state to projection window
    this.safeBroadcast({ type: 'state', state: this.state });
  }

  /** Notify the engine that the currently playing song has changed (for auto-song mode) */
  notifySongChange(songId: string): void {
    if (songId === this.lastSongId) return;
    this.lastSongId = songId;

    if (this.state.mode === 'auto-song' && this.state.running) {
      this.randomVisualization();
    }
  }

  /** Handle canvas resize */
  handleResize(width: number, height: number): void {
    if (this.canvas) {
      this.canvas.width = width;
      this.canvas.height = height;
    }
    this.activeViz?.resize(width, height);

    if (this.outgoingCanvas) {
      this.outgoingCanvas.width = width;
      this.outgoingCanvas.height = height;
    }
    if (this.transitionCanvas) {
      this.transitionCanvas.width = width;
      this.transitionCanvas.height = height;
    }
  }

  // ── Private: Render Loop ────────────────────────────────────────────────

  private renderLoop = (): void => {
    this.animFrameId = requestAnimationFrame(this.renderLoop);

    if (!this.canvas || !this.activeViz) return;
    if (this.state.frozen) return;

    // Get audio frame data (or silent data if no audio)
    const frame = this.getFrameData();

    // Blackout: just clear to black
    if (this.state.blackout) {
      const ctx = this.canvas.getContext('2d');
      if (ctx) {
        ctx.fillStyle = '#000';
        ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
      }
      this.broadcastFrame(frame);
      return;
    }

    // Render active visualization
    this.activeViz.render(frame);

    // Handle crossfade transition
    if (this.transitionActive && this.outgoingViz && this.outgoingCanvas && this.transitionCtx && this.transitionCanvas) {
      this.outgoingViz.render(frame);
      this.transitionProgress += (1 / (this.state.crossfadeSec * 60));

      if (this.transitionProgress >= 1) {
        // Transition complete
        this.outgoingViz.destroy();
        this.outgoingViz = null;
        this.transitionActive = false;
      } else {
        // Composite: blend outgoing and incoming
        const ctx = this.canvas.getContext('2d');
        if (ctx) {
          // The active viz has already rendered to its canvas.
          // For canvas2d viz, we just need opacity overlay of outgoing.
          // Save current canvas content, draw outgoing on top with fading alpha.
          this.transitionCanvas.width = this.canvas.width;
          this.transitionCanvas.height = this.canvas.height;
          this.transitionCtx!.clearRect(0, 0, this.canvas.width, this.canvas.height);
          this.transitionCtx!.globalAlpha = 1 - this.transitionProgress;
          this.transitionCtx!.drawImage(this.outgoingCanvas, 0, 0);
          this.transitionCtx!.globalAlpha = this.transitionProgress;
          this.transitionCtx!.drawImage(this.canvas, 0, 0);
          ctx.drawImage(this.transitionCanvas, 0, 0);
        }
      }
    }

    // Apply brightness
    if (this.state.brightness < 1) {
      const ctx = this.canvas.getContext('2d');
      if (ctx) {
        ctx.fillStyle = `rgba(0, 0, 0, ${1 - this.state.brightness})`;
        ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
      }
    }

    // Broadcast frame data to projection window
    this.broadcastFrame(frame);

    // FPS counter
    this.fpsFrames++;
    if (performance.now() - this.fpsLastTime > 1000) {
      this.currentFps = this.fpsFrames;
      this.fpsFrames = 0;
      this.fpsLastTime = performance.now();
    }
  };

  private getFrameData(): FrameData {
    if (this.analyzer) {
      return this.analyzer.getFrameData();
    }

    // No audio connected — return silent frame data
    const now = performance.now();
    return {
      frequencyData: this.silentFreqData,
      timeDomainData: this.silentTimeData,
      bassEnergy: 0,
      midEnergy: 0,
      trebleEnergy: 0,
      overallEnergy: 0,
      isBeat: false,
      beatIntensity: 0,
      bpm: 0,
      timestamp: now,
      deltaTime: 16.67,
      elapsed: now,
    };
  }

  // ── Broadcast counters ─────────────────────────────────────────────────
  private broadcastStateCounter = 0;
  private broadcastFrameToggle = false;

  private broadcastFrame(frame: FrameData): void {
    // Broadcast full state every ~60 frames (1s) so projection windows pick it up
    this.broadcastStateCounter++;
    if (this.broadcastStateCounter >= 60) {
      this.broadcastStateCounter = 0;
      this.safeBroadcast({ type: 'state', state: this.state });
    }

    // Throttle frame broadcasts to every other frame (~30fps) to reduce overhead
    this.broadcastFrameToggle = !this.broadcastFrameToggle;
    if (this.broadcastFrameToggle) return;

    // Send full frame data including frequency arrays (converted to regular arrays for serialization)
    this.safeBroadcast({
      type: 'frame',
      frequencyData: Array.from(frame.frequencyData),
      timeDomainData: Array.from(frame.timeDomainData),
      bassEnergy: frame.bassEnergy,
      midEnergy: frame.midEnergy,
      trebleEnergy: frame.trebleEnergy,
      overallEnergy: frame.overallEnergy,
      isBeat: frame.isBeat,
      beatIntensity: frame.beatIntensity,
      bpm: frame.bpm,
    });
  }

  // ── Private: Transitions ────────────────────────────────────────────────

  private startTransition(newViz: Visualization): void {
    if (!this.canvas || !this.outgoingCanvas) return;

    // Move current viz to outgoing
    this.outgoingViz?.destroy();
    this.outgoingViz = this.activeViz;

    // Re-init outgoing on the offscreen canvas
    if (this.outgoingViz) {
      this.outgoingCanvas.width = this.canvas.width;
      this.outgoingCanvas.height = this.canvas.height;
      this.outgoingViz.init(this.outgoingCanvas);
      this.outgoingViz.resize(this.canvas.width, this.canvas.height);
    }

    // Init new viz on the main canvas
    this.activeViz = newViz;
    this.activeViz.init(this.canvas);
    this.activeViz.resize(this.canvas.width, this.canvas.height);

    this.transitionProgress = 0;
    this.transitionActive = true;
  }

  // ── Private: Auto Rotation ──────────────────────────────────────────────

  private startAutoRotation(): void {
    if (this.state.mode === 'auto-timed') {
      this.autoTimerInterval = setInterval(() => {
        if (this.state.running && !this.state.blackout && !this.state.frozen) {
          this.randomVisualization();
        }
      }, this.state.timedIntervalSec * 1000);
    }
    // auto-song and auto-beat are handled via notifySongChange() and beat detection
  }

  private stopAutoRotation(): void {
    if (this.autoTimerInterval) {
      clearInterval(this.autoTimerInterval);
      this.autoTimerInterval = null;
    }
  }

  // ── Private: Helpers ────────────────────────────────────────────────────

  /** Safe wrapper for BroadcastChannel postMessage — silently ignores closed channel errors */
  private safeBroadcast(data: unknown): void {
    try {
      this.channel?.postMessage(data);
    } catch {
      // Channel is closed or unavailable — ignore
    }
  }

  private getPool(): Visualization[] {
    return getAllVisualizationsWithCustom().filter(v => this.state.poolIds.includes(v.id));
  }
}
